import { CookieService } from './CookieService';

export interface IUserData {
  userName: string;
  displayName: string;
  emailAddress: string;
  givenName: string;
  surname: string;
  employeeId: string;
  allRoles: string;
  allowedRoles: string;
  initials: string;
  sessionId: string;
  expires: string;
  expiresEpoch: number;
}

interface AuthData {
  user: IUserData;
  accessToken: string;
}

class UserService {
  private static readonly authCookieOptions = { path: '/', secure: false };

  private static readonly authTokenCookieKey = 'authToken';

  private static readonly authUserCookieKey = 'authUser';

  private static readonly authTokenSwitchCookieKey = 'authTokenSwitch';

  private static readonly authUserSwitchCookieKey = 'authUserSwitch';

  private static ParseUserCookie = (
    data: string | undefined
  ): IUserData | null => {
    if (!data) {
      return null;
    }

    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  };

  public static GetUser = (): IUserData | null => {
    return UserService.ParseUserCookie(
      CookieService.GetCookie(UserService.authUserCookieKey)
    );
  };

  public static GetToken = (): string | null => {
    const data = CookieService.GetCookie(UserService.authTokenCookieKey);
    if (data) {
      return data;
    }
    return null;
  };

  public static GetSwitchUser = (): IUserData | null => {
    return UserService.ParseUserCookie(
      CookieService.GetCookie(UserService.authUserSwitchCookieKey)
    );
  };

  public static GetSwitchToken = (): string | null => {
    const data = CookieService.GetCookie(UserService.authTokenSwitchCookieKey);
    if (data) {
      return data;
    }
    return null;
  };
  
  public static HasImpersonationSwitch = (): boolean => {
    return !!UserService.GetSwitchToken() && !!UserService.GetSwitchUser();
  };

  public static SetUser = (data: AuthData) => {
    CookieService.SaveCookie(
      UserService.authUserCookieKey,
      JSON.stringify(data.user),
      UserService.authCookieOptions
    );
    CookieService.SaveCookie(
      UserService.authTokenCookieKey,
      data.accessToken,
      UserService.authCookieOptions
    );
  };

  public static SaveImpersonationSwitch = (): boolean => {
    if (UserService.HasImpersonationSwitch()) {
      return false;
    }

    const currentToken = UserService.GetToken();
    const currentUser = CookieService.GetCookie(UserService.authUserCookieKey);

    if (!currentToken || !currentUser) {
      return false;
    }

    CookieService.SaveCookie(
      UserService.authTokenSwitchCookieKey,
      currentToken,
      UserService.authCookieOptions
    );
    CookieService.SaveCookie(
      UserService.authUserSwitchCookieKey,
      currentUser,
      UserService.authCookieOptions
    );

    return true;
  };

  public static RestoreImpersonationSwitch = (): boolean => {
    if (typeof window === 'undefined') return false;
    const switchToken = UserService.GetSwitchToken();
    const switchUser = CookieService.GetCookie(UserService.authUserSwitchCookieKey);

    if (!switchToken || !switchUser) {
      return false;
    }

    CookieService.SaveCookie(
      UserService.authTokenCookieKey,
      switchToken,
      UserService.authCookieOptions
    );
    CookieService.SaveCookie(
      UserService.authUserCookieKey,
      switchUser,
      UserService.authCookieOptions
    );
    UserService.ClearImpersonationSwitch();

    return true;
  };

  public static ClearImpersonationSwitch = () => {
    CookieService.DeleteCookie(
      UserService.authTokenSwitchCookieKey,
      UserService.authCookieOptions
    );
    CookieService.DeleteCookie(
      UserService.authUserSwitchCookieKey,
      UserService.authCookieOptions
    );
  };

  public static ClearAuthenticationCookies = () => {
    CookieService.DeleteCookie(
      UserService.authTokenCookieKey,
      UserService.authCookieOptions
    );
    CookieService.DeleteCookie(
      UserService.authUserCookieKey,
      UserService.authCookieOptions
    );
    UserService.ClearImpersonationSwitch();
  };
}

export { UserService };
